import React, { useState, useEffect } from "react";
import axios from "axios";
import { Banknote, Plus, Trash2, CheckCircle2, X, Sparkles, Users, Gauge, AlertTriangle, Printer, FileText, Send, CalendarDays } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const monthOptions = (() => {
  const opts = [];
  const now = new Date();
  for (let i = -3; i <= 2; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const kode = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ kode, label: d.toLocaleDateString("id-ID", { month: "long", year: "numeric" }) });
  }
  return opts;
})();

const emptyForm = () => ({
  karyawan_id: "",
  gaji_pokok: 3000000,
  potongan: 0,
  keterangan: "",
  tunjangan_items: [{ nama: "Tunjangan Umum", nominal: 200000 }],
});

export const KeuanganGajiPage = () => {
  const currentKode = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const [list, setList] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [periode, setPeriode] = useState(currentKode);
  const [showAdd, setShowAdd] = useState(false);
  const [notif, setNotif] = useState("");
  const [proyeksi, setProyeksi] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [comp, setComp] = useState({});
  const [allGaji, setAllGaji] = useState([]);
  const [showRekap, setShowRekap] = useState(false);
  const [rekapBulan, setRekapBulan] = useState("all");
  const [rekapTahun, setRekapTahun] = useState(String(new Date().getFullYear()));
  const [postTarget, setPostTarget] = useState(null);
  const [postTanggal, setPostTanggal] = useState("");
  const [postBusy, setPostBusy] = useState(false);

  const fetchComp = async () => {
    try { const res = await axios.get(`${BACKEND_URL}/api/pengaturan/perusahaan`, { withCredentials: true }); setComp(res.data || {}); } catch (e) { /* ignore */ }
  };
  const openRekap = async () => {
    try { const res = await axios.get(`${BACKEND_URL}/api/gaji`, { withCredentials: true }); setAllGaji(res.data || []); } catch (e) { /* ignore */ }
    setShowRekap(true);
  };

  const fetchData = async () => {
    const res = await axios.get(`${BACKEND_URL}/api/gaji?periode=${periode}`, { withCredentials: true });
    setList(res.data);
  };
  const fetchKaryawan = async () => {
    const res = await axios.get(`${BACKEND_URL}/api/master/karyawan`, { withCredentials: true });
    setKaryawan(res.data || []);
  };
  const fetchProyeksi = async () => {
    const res = await axios.get(`${BACKEND_URL}/api/gaji/proyeksi?periode=${periode}`, { withCredentials: true });
    setProyeksi(res.data);
  };

  const refresh = () => { fetchData(); fetchProyeksi(); };

  useEffect(() => { refresh(); }, [periode]);
  useEffect(() => { fetchKaryawan(); fetchComp(); }, []);

  const notify = (m) => { setNotif(m); setTimeout(() => setNotif(""), 4000); };
  const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

  const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const escH = (v) => String(v ?? "-").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const periodeLabel = (kode) => { const [y, m] = String(kode || "").split("-"); return `${MONTHS[parseInt(m, 10) - 1] || m} ${y}`; };
  const compName = () => (comp.reseller_enabled && comp.reseller_nama ? comp.reseller_nama : (comp.nama_isp || "Perusahaan"));
  const compAddr = () => (comp.reseller_enabled && comp.reseller_alamat ? comp.reseller_alamat : (comp.alamat || ""));
  const openPrint = (html) => { const w = window.open("", "_blank", "width=900,height=1000"); if (!w) return; w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); };

  const printSlip = (i) => {
    const items = Array.isArray(i.tunjangan_items) ? i.tunjangan_items : [];
    const tunjRows = items.length
      ? items.map((t) => `<tr><td>Tunjangan — ${escH(t.nama)}</td><td class="num">Rp ${Number(t.nominal || 0).toLocaleString("id-ID")}</td></tr>`).join("")
      : `<tr><td>Tunjangan</td><td class="num">Rp ${Number(i.tunjangan || 0).toLocaleString("id-ID")}</td></tr>`;
    const html = `<!DOCTYPE html><html><head><title>Slip Gaji ${escH(i.karyawan_nama)}</title><style>
      *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;padding:24px}
      .box{max-width:520px;margin:0 auto;border:1px solid #cbd5e1;border-radius:8px;padding:20px}
      .head{text-align:center;border-bottom:2px solid #0f172a;padding-bottom:10px;margin-bottom:12px}
      .head h2{margin:0;font-size:16px;color:#155e75} .head p{margin:2px 0;font-size:10px;color:#64748b}
      .title{text-align:center;font-weight:800;font-size:13px;letter-spacing:1px;margin:8px 0}
      .info{font-size:11px;margin:8px 0;display:flex;justify-content:space-between}
      table{width:100%;border-collapse:collapse;font-size:11px;margin-top:6px}
      td{padding:6px 4px;border-bottom:1px solid #e2e8f0} .num{text-align:right;font-family:monospace}
      .total td{font-weight:900;border-top:2px solid #0f172a;font-size:12px}
      .status{margin-top:10px;font-size:11px;color:#334155}
      .sign{margin-top:26px;text-align:right;font-size:11px}
      .sign .line{margin-top:38px;border-top:1px solid #94a3b8;display:inline-block;min-width:160px;padding-top:4px}
      @media print{body{padding:0}}
    </style></head><body><div class="box">
      <div class="head"><h2>${escH(compName())}</h2><p>${escH(compAddr())}</p></div>
      <div class="title">SLIP GAJI KARYAWAN</div>
      <div class="info"><span>Periode: <b>${escH(periodeLabel(i.periode))}</b></span><span>Status: <b>${i.status === "PAID" ? "DIBAYAR" : "BELUM DIBAYAR"}</b></span></div>
      <div class="info"><span>Nama: <b>${escH(i.karyawan_nama)}</b></span><span>Jabatan: ${escH(i.jabatan || "-")}</span></div>
      <table>
        <tr><td>Gaji Pokok</td><td class="num">Rp ${Number(i.gaji_pokok || 0).toLocaleString("id-ID")}</td></tr>
        ${tunjRows}
        <tr><td>Potongan</td><td class="num">- Rp ${Number(i.potongan || 0).toLocaleString("id-ID")}</td></tr>
        <tr class="total"><td>TOTAL TAKE HOME PAY</td><td class="num">Rp ${Number(i.total || 0).toLocaleString("id-ID")}</td></tr>
      </table>
      ${i.tanggal_bayar ? `<p class="status">Dibayar pada ${escH(i.tanggal_bayar)} via ${escH(i.metode_bayar || "-")}</p>` : ""}
      ${i.keterangan ? `<p class="status">Keterangan: ${escH(i.keterangan)}</p>` : ""}
      <div class="sign">Penerima,<div class="line">${escH(i.karyawan_nama)}</div></div>
    </div></body></html>`;
    openPrint(html);
  };

  const rekapYears = Array.from(new Set(allGaji.map((g) => String(g.periode || "").slice(0, 4)).filter(Boolean)));
  if (!rekapYears.includes(String(new Date().getFullYear()))) rekapYears.push(String(new Date().getFullYear()));
  rekapYears.sort((a, b) => b.localeCompare(a));

  const printRekap = () => {
    const rows2 = allGaji
      .filter((g) => {
        const kode = String(g.periode || "");
        const okT = rekapTahun === "all" || kode.slice(0, 4) === rekapTahun;
        const okB = rekapBulan === "all" || kode.slice(5, 7) === rekapBulan;
        return okT && okB;
      })
      .sort((a, b) => (a.periode === b.periode ? String(a.karyawan_nama || "").localeCompare(String(b.karyawan_nama || "")) : String(b.periode || "").localeCompare(String(a.periode || ""))));
    const total = rows2.reduce((s, i) => s + Number(i.total || 0), 0);
    const paid = rows2.filter((i) => i.status === "PAID").reduce((s, i) => s + Number(i.total || 0), 0);
    const bulanLabel = rekapBulan === "all" ? "Semua Bulan" : MONTHS[parseInt(rekapBulan, 10) - 1];
    const tahunLabel = rekapTahun === "all" ? "Semua Tahun" : rekapTahun;
    const now = new Date().toLocaleString("id-ID");
    const rows = rows2.length
      ? rows2.map((i, idx) => `<tr><td>${idx + 1}</td><td>${escH(periodeLabel(i.periode))}</td><td>${escH(i.karyawan_nama)}</td><td>${escH(i.jabatan || "-")}</td><td class="num">Rp ${Number(i.gaji_pokok || 0).toLocaleString("id-ID")}</td><td class="num">Rp ${Number(i.tunjangan || 0).toLocaleString("id-ID")}</td><td class="num">Rp ${Number(i.potongan || 0).toLocaleString("id-ID")}</td><td class="num">Rp ${Number(i.total || 0).toLocaleString("id-ID")}</td><td>${i.status === "PAID" ? "DIBAYAR" : "BELUM"}</td></tr>`).join("")
      : `<tr><td colspan="9" style="text-align:center;color:#64748b;padding:16px">Tidak ada data gaji untuk filter ini.</td></tr>`;
    const html = `<!DOCTYPE html><html><head><title>Rekap Pembayaran Gaji</title><style>
      *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;padding:24px}
      h1{font-size:18px;margin:0 0 2px} .sub{font-size:11px;color:#64748b;margin:0}
      .meta{margin:12px 0;font-size:11px;color:#334155} .meta b{color:#0f172a}
      table{width:100%;border-collapse:collapse;margin-top:10px;font-size:11px}
      th{background:#f1f5f9;text-align:left;padding:8px;border-bottom:2px solid #cbd5e1}
      td{padding:7px 8px;border-bottom:1px solid #e2e8f0} .num{text-align:right;font-family:monospace}
      tfoot td{font-weight:900;border-top:2px solid #0f172a;font-size:12px}
      @media print{body{padding:0}}
    </style></head><body>
      <h1>Rekap Pembayaran Gaji Karyawan</h1>
      <p class="sub">${escH(compName())}</p>
      <div class="meta">Bulan: <b>${escH(bulanLabel)}</b> &nbsp;&bull;&nbsp; Tahun: <b>${escH(tahunLabel)}</b> &nbsp;&bull;&nbsp; Jumlah Slip: <b>${rows2.length}</b> &nbsp;&bull;&nbsp; Sudah Dibayar: <b>Rp ${paid.toLocaleString("id-ID")}</b><br/>Dicetak: ${escH(now)}</div>
      <table>
        <thead><tr><th>#</th><th>Periode</th><th>Karyawan</th><th>Jabatan</th><th class="num">Gaji Pokok</th><th class="num">Tunjangan</th><th class="num">Potongan</th><th class="num">Total</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td colspan="7" style="text-align:right">TOTAL</td><td class="num">Rp ${total.toLocaleString("id-ID")}</td><td></td></tr></tfoot>
      </table>
    </body></html>`;
    openPrint(html);
  };

  // Tunjangan dinamis
  const addTunjangan = () => setForm((f) => ({ ...f, tunjangan_items: [...f.tunjangan_items, { nama: "", nominal: 0 }] }));
  const removeTunjangan = (idx) => setForm((f) => ({ ...f, tunjangan_items: f.tunjangan_items.filter((_, i) => i !== idx) }));
  const updateTunjangan = (idx, key, val) => setForm((f) => ({
    ...f,
    tunjangan_items: f.tunjangan_items.map((t, i) => (i === idx ? { ...t, [key]: val } : t)),
  }));

  const tunjanganTotal = form.tunjangan_items.reduce((a, t) => a + (Number(t.nominal) || 0), 0);
  const totalTHP = (Number(form.gaji_pokok) || 0) + tunjanganTotal - (Number(form.potongan) || 0);

  const openForm = () => { setForm(emptyForm()); setShowAdd(true); };

  const onSelectKaryawan = (id) => {
    const k = karyawan.find((x) => x.id === id);
    setForm((f) => ({ ...f, karyawan_id: id, gaji_pokok: k && k.gaji_pokok ? k.gaji_pokok : f.gaji_pokok }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const k = karyawan.find((x) => x.id === form.karyawan_id);
    if (!k) { alert("Pilih karyawan terlebih dahulu"); return; }
    const items = form.tunjangan_items
      .filter((t) => (Number(t.nominal) || 0) > 0 || (t.nama || "").trim())
      .map((t) => ({ nama: t.nama || "Tunjangan", nominal: Number(t.nominal) || 0 }));
    try {
      await axios.post(`${BACKEND_URL}/api/gaji`, {
        karyawan_id: k.id, karyawan_nama: k.nama, jabatan: k.jabatan, periode,
        gaji_pokok: Number(form.gaji_pokok) || 0,
        tunjangan_items: items,
        tunjangan: items.reduce((a, t) => a + t.nominal, 0),
        potongan: Number(form.potongan) || 0, keterangan: form.keterangan,
      }, { withCredentials: true });
      setShowAdd(false);
      setForm(emptyForm());
      refresh();
      notify("Gaji karyawan berhasil ditetapkan!");
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menyimpan gaji");
    }
  };

  const handleGenerate = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/gaji/generate`, { periode }, { withCredentials: true });
      refresh();
      notify(res.data.message);
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal generate gaji");
    }
  };

  const handleBayar = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/gaji/${id}/bayar`, { metode_bayar: "Transfer Bank" }, { withCredentials: true });
      refresh();
      notify("Gaji berhasil dibayarkan!");
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal membayar gaji");
    }
  };

  const todayStr = () => {
    const d = new Date();
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };

  const handlePosting = (i) => {
    setPostTarget(i);
    setPostTanggal(todayStr());
  };

  const confirmPosting = async () => {
    if (!postTarget) return;
    setPostBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/gaji/${postTarget.id}/posting`, { tanggal: postTanggal }, { withCredentials: true });
      setPostTarget(null);
      refresh();
      notify(res.data.message || "Gaji berhasil diposting ke Pengeluaran!");
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal memposting gaji");
    } finally {
      setPostBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus data gaji ini?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/gaji/${id}`, { withCredentials: true });
      refresh();
      notify("Slip gaji dihapus");
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menghapus slip gaji");
    }
  };

  const totalGaji = list.reduce((a, i) => a + (i.total || 0), 0);
  const totalPaid = list.filter((i) => i.status === "PAID").reduce((a, i) => a + (i.total || 0), 0);
  const over = proyeksi && proyeksi.over;

  return (
    <div className="space-y-6" data-testid="gaji-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div>
          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-wider">Payroll</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Gaji Karyawan</h1>
          <p className="text-xs text-slate-400 mt-0.5">Tetapkan gaji per karyawan & sinkronkan dengan batas anggaran operasional.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="gaji-periode" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold">
            {monthOptions.map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
          </select>
          <button onClick={handleGenerate} data-testid="gaji-generate-btn" className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl flex items-center gap-1.5 border border-slate-700">
            <Sparkles className="w-4 h-4 text-amber-400" /> Generate
          </button>
          <button onClick={openRekap} data-testid="cetak-rekap-btn" className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl flex items-center gap-1.5 border border-slate-700">
            <FileText className="w-4 h-4 text-cyan-400" /> Cetak Rekap
          </button>
          <button onClick={openForm} data-testid="add-gaji-btn" className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-600/30 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Tetapkan Gaji
          </button>
        </div>
      </div>

      {notif && <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl text-xs flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {notif}</div>}

      {/* Sinkronisasi Anggaran (jumlah karyawan x total gaji vs batas operasional) */}
      {proyeksi && (
        <div className={`p-5 rounded-2xl border shadow-xl ${over ? "bg-rose-950/30 border-rose-500/40" : "bg-[#0e1628] border-slate-800"}`} data-testid="gaji-sinkronisasi">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Gauge className="w-4 h-4 text-orange-400" /> Sinkronisasi Anggaran Gaji</h3>
            {over
              ? <span className="px-2.5 py-1 rounded-full bg-rose-500/20 text-rose-300 text-[10px] font-bold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> MELEBIHI BATAS</span>
              : <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold">DALAM BATAS</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-xl bg-slate-950/60">
              <p className="text-[11px] text-slate-400 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Jumlah Karyawan Aktif</p>
              <p className="text-lg font-black text-white mt-1">{proyeksi.jumlah_karyawan}</p>
              <p className="text-[10px] text-slate-500">{proyeksi.jumlah_slip} slip periode ini</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-950/60">
              <p className="text-[11px] text-slate-400">Total Gaji (Karyawan × Gaji)</p>
              <p className="text-lg font-black text-cyan-400 mt-1 font-mono">{rp(proyeksi.total_gaji)}</p>
              <p className="text-[10px] text-slate-500">rata-rata {rp(proyeksi.rata_rata)}</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-950/60">
              <p className="text-[11px] text-slate-400">Batas Maks Gaji ({proyeksi.gaji_max_persen}%)</p>
              <p className="text-lg font-black text-amber-400 mt-1 font-mono">{rp(proyeksi.batas)}</p>
              <p className="text-[10px] text-slate-500">dari pendapatan {rp(proyeksi.pendapatan)}</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-950/60">
              <p className="text-[11px] text-slate-400">{over ? "Kelebihan" : "Sisa Anggaran"}</p>
              <p className={`text-lg font-black mt-1 font-mono ${over ? "text-rose-400" : "text-emerald-400"}`}>{rp(Math.abs(proyeksi.sisa))}</p>
              <p className="text-[10px] text-slate-500">{proyeksi.persen_terpakai.toFixed(0)}% terpakai</p>
            </div>
          </div>
          <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden mt-4">
            <div className={`h-full transition-all ${over ? "bg-rose-500" : proyeksi.persen_terpakai > 80 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(proyeksi.persen_terpakai, 100)}%` }} />
          </div>
          {over && <p className="text-[11px] text-rose-400 font-semibold mt-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Total gaji melebihi batas maksimal anggaran operasional sebesar {rp(Math.abs(proyeksi.sisa))}. Kurangi gaji/tunjangan atau naikkan batas di Manajemen Operasional.</p>}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-[11px] text-slate-400">Total Slip</p><p className="text-lg font-black text-white mt-1">{list.length}</p></div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-[11px] text-slate-400">Total Gaji Periode</p><p className="text-lg font-black text-cyan-400 mt-1 font-mono">{rp(totalGaji)}</p></div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-[11px] text-slate-400">Sudah Dibayar</p><p className="text-lg font-black text-emerald-400 mt-1 font-mono">{rp(totalPaid)}</p></div>
      </div>

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead><tr className="border-b border-slate-800 text-slate-400 font-semibold">
            <th className="py-2.5 px-3">Karyawan</th><th className="py-2.5 px-3">Gaji Pokok</th><th className="py-2.5 px-3">Tunjangan</th><th className="py-2.5 px-3">Potongan</th><th className="py-2.5 px-3">Total</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3 text-right">Aksi</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-800/60">
            {list.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">Belum ada data gaji untuk periode ini. Klik Generate atau Tetapkan Gaji.</td></tr>}
            {list.map((i) => (
              <tr key={i.id} className="hover:bg-slate-900/40" data-testid={`gaji-row-${i.id}`}>
                <td className="py-3 px-3"><p className="font-bold text-white">{i.karyawan_nama}</p><span className="text-[10px] text-slate-400">{i.jabatan}</span></td>
                <td className="py-3 px-3 text-slate-300 font-mono">{rp(i.gaji_pokok)}</td>
                <td className="py-3 px-3 text-emerald-400 font-mono">
                  {rp(i.tunjangan)}
                  {Array.isArray(i.tunjangan_items) && i.tunjangan_items.length > 0 && (
                    <span className="block text-[10px] text-slate-500 font-sans">{i.tunjangan_items.map((t) => t.nama).join(", ")}</span>
                  )}
                </td>
                <td className="py-3 px-3 text-rose-400 font-mono">{rp(i.potongan)}</td>
                <td className="py-3 px-3 font-mono font-bold text-cyan-400">{rp(i.total)}</td>
                <td className="py-3 px-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${i.status === "PAID" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{i.status === "PAID" ? "DIBAYAR" : "BELUM"}</span></td>
                <td className="py-3 px-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => printSlip(i)} data-testid={`slip-gaji-${i.id}`} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 rounded-lg text-[11px] font-bold flex items-center gap-1"><Printer className="w-3 h-3" /> Slip</button>
                    {i.posted
                      ? <span data-testid={`posted-gaji-${i.id}`} className="px-2 py-1 bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 rounded-lg text-[10px] font-bold" title="Sudah masuk Pengeluaran">TERPOSTING</span>
                      : <button onClick={() => handlePosting(i)} data-testid={`posting-gaji-${i.id}`} className="px-2 py-1 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-[11px] font-bold flex items-center gap-1"><Send className="w-3 h-3" /> Posting</button>}
                    {i.status !== "PAID" && <button onClick={() => handleBayar(i.id)} data-testid={`bayar-gaji-${i.id}`} className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[11px] font-bold">Bayar</button>}
                    <button onClick={() => handleDelete(i.id)} className="p-1.5 text-rose-400 hover:bg-rose-950/60 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><Banknote className="w-4 h-4 text-emerald-400" /> Tetapkan Gaji — {monthOptions.find((o) => o.kode === periode)?.label}</h3>
              <button onClick={() => setShowAdd(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-300 mb-1">Karyawan *</label>
                <select required value={form.karyawan_id} onChange={(e) => onSelectKaryawan(e.target.value)} data-testid="gaji-karyawan-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                  <option value="">-- Pilih Karyawan --</option>
                  {karyawan.map((k) => <option key={k.id} value={k.id}>{k.nama} ({k.jabatan})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-300 mb-1">Gaji Pokok</label>
                <input type="number" value={form.gaji_pokok} onChange={(e) => setForm({ ...form, gaji_pokok: e.target.value })} data-testid="gaji-pokok-input" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono" />
              </div>

              {/* Tunjangan dinamis */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs text-slate-300">Tunjangan</label>
                  <button type="button" onClick={addTunjangan} data-testid="add-tunjangan-btn" className="px-2 py-1 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 rounded-lg text-[10px] font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Tambah Tunjangan</button>
                </div>
                <div className="space-y-2">
                  {form.tunjangan_items.map((t, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`tunjangan-row-${idx}`}>
                      <input type="text" placeholder="Nama (mis. Lembur)" value={t.nama} onChange={(e) => updateTunjangan(idx, "nama", e.target.value)} data-testid={`tunjangan-nama-${idx}`} className="flex-1 px-2 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" />
                      <input type="number" placeholder="0" value={t.nominal} onChange={(e) => updateTunjangan(idx, "nominal", e.target.value)} data-testid={`tunjangan-nominal-${idx}`} className="w-28 px-2 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono" />
                      <button type="button" onClick={() => removeTunjangan(idx)} className="p-1.5 text-rose-400 hover:bg-rose-950/60 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  {form.tunjangan_items.length === 0 && <p className="text-[10px] text-slate-500">Belum ada tunjangan. Klik "Tambah Tunjangan".</p>}
                </div>
                <div className="mt-1.5 text-[11px] text-slate-400 flex justify-between"><span>Total Tunjangan</span><span className="font-mono text-emerald-400 font-bold">{rp(tunjanganTotal)}</span></div>
              </div>

              <div>
                <label className="block text-xs text-slate-300 mb-1">Potongan</label>
                <input type="number" value={form.potongan} onChange={(e) => setForm({ ...form, potongan: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono" />
              </div>

              <div className="p-2.5 bg-slate-950 rounded-xl text-xs flex justify-between"><span className="text-slate-400">Total Take Home Pay</span><span className="font-mono font-bold text-cyan-400" data-testid="gaji-thp">{rp(totalTHP)}</span></div>
              <div><label className="block text-xs text-slate-300 mb-1">Keterangan</label><input type="text" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" /></div>
              <div className="pt-1 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Batal</button>
                <button type="submit" data-testid="save-gaji-btn" className="px-4 py-1.5 bg-emerald-600 text-white rounded-xl text-xs font-bold">Simpan</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRekap && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4" data-testid="rekap-modal">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-400" /> Cetak Rekap Pembayaran Gaji</h3>
              <button onClick={() => setShowRekap(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-slate-400">Pilih bulan &amp; tahun untuk rekap, atau pilih "Semua Bulan" untuk mencetak seluruh periode.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-300 mb-1">Bulan</label>
                <select value={rekapBulan} onChange={(e) => setRekapBulan(e.target.value)} data-testid="rekap-bulan-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                  <option value="all">Semua Bulan</option>
                  {MONTHS.map((m, idx) => <option key={idx} value={String(idx + 1).padStart(2, "0")}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-300 mb-1">Tahun</label>
                <select value={rekapTahun} onChange={(e) => setRekapTahun(e.target.value)} data-testid="rekap-tahun-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                  <option value="all">Semua Tahun</option>
                  {rekapYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div className="pt-1 flex justify-end gap-2">
              <button type="button" onClick={() => setShowRekap(false)} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Tutup</button>
              <button type="button" onClick={printRekap} data-testid="print-rekap-btn" className="px-4 py-1.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5"><Printer className="w-3.5 h-3.5" /> Cetak Rekap</button>
            </div>
          </div>
        </div>
      )}

      {postTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-sm shadow-2xl p-5 space-y-4" data-testid="posting-gaji-modal">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><Send className="w-4 h-4 text-cyan-400" /> Posting Gaji ke Pengeluaran</h3>
              <button onClick={() => setPostTarget(null)} data-testid="posting-gaji-close" className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 bg-slate-950 rounded-xl text-xs space-y-1.5">
              <div className="flex justify-between"><span className="text-slate-400">Karyawan</span><span className="text-slate-100 font-semibold">{postTarget.karyawan_nama}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Nominal</span><span className="text-cyan-400 font-mono font-bold">{rp(postTarget.total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Kategori</span><span className="text-slate-100">Gaji Karyawan/Teknisi</span></div>
            </div>
            <div>
              <label className="block text-xs text-slate-300 mb-1 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-slate-400" /> Tanggal Posting *</label>
              <input type="date" required value={postTanggal} onChange={(e) => setPostTanggal(e.target.value)} data-testid="posting-gaji-tanggal" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono" />
              <p className="text-[10px] text-slate-500 mt-1.5">Tanggal ini dipakai sebagai tanggal pengeluaran & menentukan periode pada Laporan Laba Rugi.</p>
            </div>
            <div className="pt-1 flex justify-end gap-2">
              <button type="button" onClick={() => setPostTarget(null)} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Batal</button>
              <button type="button" onClick={confirmPosting} disabled={!postTanggal || postBusy} data-testid="posting-gaji-confirm" className="px-4 py-1.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 text-white rounded-xl text-xs font-bold flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> {postBusy ? "Memproses..." : "Posting Sekarang"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
